import { Box, FormControl, InputLabel, MenuItem, Select, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { BarChart } from "@mui/x-charts/BarChart";
import { formatHoursMinutes } from "@repo/legacy/components/helper/Index";
import { useGetMyProjectWorktimeOverviewLazyQuery } from "@repo/server-api";
import dayjs from "dayjs";
import { useTranslation } from "next-i18next";
import { useEffect, useMemo, useState } from "react";

export const ProjectWorktimeOverview = () => {
	const { t } = useTranslation();

	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
	const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
	const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

	const [data, setData] = useState<
		{
			day: string;
			[key: string]: string | number;
		}[]
	>([]);

	const [getMyProjectWorktimeOverview] = useGetMyProjectWorktimeOverviewLazyQuery({
		fetchPolicy: "no-cache",
	});

	const [numberOfWeeks, setNumberOfWeeks] = useState<number>(1);

	useEffect(() => {
		const start = dayjs()
			.add(-numberOfWeeks + 1, "week")
			.startOf("week");
		const end = dayjs().endOf("week");
		getMyProjectWorktimeOverview({
			variables: {
				startDate: start.format("YYYY-MM-DD"),
				endDate: end.format("YYYY-MM-DD"),
			},
		}).then((result) => {
			const res = result.data?.myProjectWorktimeOverview || [];
			const chartData = res.map((item) => {
				let response: { day: string; [key: string]: string | number } = {
					day: dayjs(item.date).format("DD.MM. dd"),
				};
				item.entries.forEach((entry) => {
					const id = entry.projectId + "-" + entry.projectPositionId;
					response[id] = entry.totalMinutes;
					response["label-" + id] = entry.projectName + " - " + entry.projectPositionName;
				});

				return response;
			});
			setData(chartData);
		});
	}, [getMyProjectWorktimeOverview, numberOfWeeks]);

	const series = useMemo(() => {
		const seriesKeys = Array.from(
			new Set(
				data.flatMap((row) =>
					Object.keys(row).filter((key) => key !== "day" && !key.startsWith("label-")),
				),
			),
		);

		const series = seriesKeys.map((key) => ({
			dataKey: key,
			type: "bar" as const,
			label: data.find((d) => d[`label-${key}`])?.[`label-${key}`] as string | undefined,
			valueFormatter: (value: number | null) => formatHoursMinutes(value ?? 0),
			color: theme.graphPalette[seriesKeys.indexOf(key) % theme.graphPalette.length],
		}));
		return series;
	}, [data]);

	return (
		<Box
			sx={{
				border: "1px solid #ccc",
				paddingTop: 0,
				paddingBottom: 2,
				paddingLeft: 2,
				paddingRight: 2,
				borderRadius: 2,
				marginRight: 2,
				height: "100%",
			}}>
			<h3 className="purpleHeader">
				{t("my-hours")}
				<FormControl sx={{ width: "30%", float: "right", marginRight: "10px" }} size="small">
					<InputLabel id="weeks">{t("number-of-weeks")}</InputLabel>
					<Select
						labelId="weeks"
						id="select-weeks"
						value={numberOfWeeks}
						label={t("number-of-weeks")}
						onChange={(event) => {
							setNumberOfWeeks(event.target.value as number);
						}}>
						<MenuItem value={1}>{t("1-week")}</MenuItem>
						<MenuItem value={2}>{t("2-weeks")}</MenuItem>
						<MenuItem value={4}>{t("4-weeks")}</MenuItem>
					</Select>
				</FormControl>
			</h3>
			<BarChart
				localeText={{ noData: t("no-data-to-display") }}
				xAxis={[
					{
						dataKey: "day",
						label: t("days"),
						height: 75,
					},
				]}
				height={300}
				dataset={data}
				series={series}
				hideLegend
				yAxis={[
					{
						valueFormatter: (value: number) => formatHoursMinutes(value),
						label: t("hours"),
						width: 85,
					},
				]}
			/>
		</Box>
	);
};
